import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig, GridRow, GridRowProblem } from '@inavista/components/grid';

// Return order popup → Items on @inavista/components. Same columns, labels (red * on Item, Quantity, Reason), order,
// widths, links (Item, Description), Reason shown as its text, Delete command and toolbar (Columns, Add Item, Search,
// Excel export for AllGrid_Toolbar_Excel_Export, Clear filters) as the Syncfusion grid it replaces. Rows are the popup's
// own items not deleted (LocalRowsGridApi), saved with the popup. Each editable cell shows its editor
// (CellEditorHost, @inavista/components controls), so the item / on-hand rules inside them run as before.

export const RETURN_ORDER_ITEMS_URL = 'page://return-order-items';
export const RETURN_ORDER_ITEMS_PERSISTENCE_KEY = 'rims.service.return-order-items';
export const ADD_ITEM_ACTION = 'add-item';
export const DELETE_ITEM_ACTION = 'Delete Item';
/** The page's row key (lineNumber changes when rows are renumbered). */
export const ROW_KEY = '__rowKey';
/** A hidden field the page writes when a row opens, so leaving it always saves it (the Syncfusion grid saved on leave). */
export const OPEN_FIELD = '__open';

const BLACK = '#000000de';
const INACTIVE = '#cccccc';
const PLUS_ICON = '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />';
const TRASH_ICON = '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />';

export const RETURN_ORDER_ITEMS_COLUMNS: [string, string][] = [
  [ROW_KEY, 'int'],
  ['itemCode', 'nvarchar'],
  ['itemDescription', 'nvarchar'],
  ['serviceItemGroup', 'nvarchar'],
  ['quantity', 'decimal'],
  ['reasonCode', 'nvarchar'],
  [OPEN_FIELD, 'nvarchar'],
];

/** The columns with a cell editor (CellEditorHost), in the old column order. */
export const RETURN_ORDER_ITEMS_EDIT_FIELDS = ['itemCode', 'itemDescription', 'serviceItemGroup', 'quantity', 'reasonCode'];

export interface ReturnOrderItemsGridContext {
  editors: Record<string, TemplateRef<unknown>>;
  canAdd: boolean;
  canExport: boolean;
  addDisabledReason: () => string | null;
  editLockReason: () => string | null;
  deleteLockReason: () => string | null;
  validate: (row: GridRow) => GridRowProblem | null;
}

export function buildReturnOrderItemsActionIcons(ctx: ReturnOrderItemsGridContext): ActionIconDef[] {
  return [{
    key: DELETE_ITEM_ACTION, label: 'Delete Item', icon: TRASH_ICON,
    colorFn: () => ctx.deleteLockReason() ? INACTIVE : BLACK,
    labelFn: () => ctx.deleteLockReason() ?? 'Delete Item',
  }];
}

export function buildReturnOrderItemsGridConfig(ctx: ReturnOrderItemsGridContext): GridConfig {
  const shown = RETURN_ORDER_ITEMS_EDIT_FIELDS;
  return {
    apiBaseUrl: RETURN_ORDER_ITEMS_URL,
    clientSide: true,
    keyColumn: ROW_KEY,
    rowHeight: 30,
    defaultColumnOrder: shown,
    hiddenByDefaultColumns: [ROW_KEY, OPEN_FIELD],
    columnLabels: {
      itemCode: 'Item', itemDescription: 'Description', serviceItemGroup: 'Service Item Group', quantity: 'Quantity',
      reasonCode: 'Reason',
    },
    // The old header asterisks (red, styles.scss): the marker only; the editors' own rules decide on save.
    requiredColumns: ['itemCode', 'quantity', 'reasonCode'],
    // Also for a user who may not edit (the old header showed them either way).
    requiredMarkers: 'always',
    // Every column left, as the old grid.
    columnAlign: Object.fromEntries(shown.map(c => [c, { horizontal: 'left' as const, header: { horizontal: 'left' as const } }])),
    // The old widths; Description had none and took the space left, so it is the only one that stretches.
    columnWidths: { itemCode: 250, itemDescription: 200, serviceItemGroup: 150, quantity: 142, reasonCode: 150 },
    stretchColumns: true,
    columnSizing: Object.fromEntries(shown.filter(c => c !== 'itemDescription').map(c => [c, { noStretch: true }])),
    // Syncfusion's 'n': grouped, up to 3 decimals.
    columnNumberFormats: { quantity: { maximumFractionDigits: 3 } },
    hyperlinkColumns: ['itemCode', 'itemDescription'],
    // Item, Service Item Group and Reason had the Excel (checkbox list) filter; the rest the menu filter.
    excelFilterColumns: ['itemCode', 'serviceItemGroup', 'reasonCode'],
    // The old Actions column was 130 wide.
    actionsColumnMinWidth: 130,
    // Rows open only for a user who may change them, as the old editSettings.allowEditing (the page rebuilds
    // the config when that changes): a row whose every cell is locked would still open, with nothing to edit.
    editMode: ctx.editLockReason() ? 'none' : 'inline',
    editSaveMode: 'row',
    // A click on a cell opens its row, as the old grid's mouseup handler did.
    editTrigger: 'click',
    editableColumns: RETURN_ORDER_ITEMS_EDIT_FIELDS,
    editorTemplates: ctx.editors,
    readOnlyCellFn: () => !!ctx.editLockReason(),
    // The Syncfusion validation rules of the columns (Reason required, Quantity's on-hand max), read from the open editors.
    rowValidator: row => ctx.validate(row),
    deleteMode: 'host',
    rowDeletable: () => false,
    toolbarActions: ctx.canAdd ? [{
      key: ADD_ITEM_ACTION, label: 'Add Item', kind: 'button', icon: PLUS_ICON,
      disabled: () => !!ctx.addDisabledReason(), disabledReason: () => ctx.addDisabledReason() ?? '',
    }] : [],
    enableColumnsPicker: true,
    enableExport: ctx.canExport,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    multiSort: true,
    emptyStateMessage: 'No records to display',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: RETURN_ORDER_ITEMS_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
